import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Box, Typography, Avatar, Badge, TextField, IconButton, Chip,
  List, ListItem, ListItemButton, ListItemAvatar, ListItemText,
  InputAdornment, Tooltip, Divider, CircularProgress, Dialog
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import SendIcon from '@mui/icons-material/Send';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import EmojiEmotionsIcon from '@mui/icons-material/EmojiEmotions';
import AddIcon from '@mui/icons-material/Add';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';
import GroupIcon from '@mui/icons-material/Group';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import DeleteIcon from '@mui/icons-material/Delete';
import MicIcon from '@mui/icons-material/Mic';
import StopIcon from '@mui/icons-material/Stop';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import DownloadIcon from '@mui/icons-material/Download';
import { useSearchParams } from 'react-router-dom';
import { useChat } from '../contexts/ChatContext';
import { useApi } from '../contexts/ApiContext';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ChatMessageItem from '../components/chat/ChatMessageItem';
import NewConversationDialog from '../components/chat/NewConversationDialog';
import EmojiPickerPopover from '../components/chat/EmojiPickerPopover';
import chatBgImg from '../images/chat-bg/chat-bg.png';

// Light theme with strong contrast
const accent = '#7C3AED';
const accentLight = 'rgba(124, 58, 237, 0.08)';
const accentMid = 'rgba(124, 58, 237, 0.15)';
const panel = {
  background: '#fff',
  border: '1px solid #d1d5db',
  boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
};

const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 2,
    background: '#f3f4f6',
    '& fieldset': { borderColor: '#d1d5db' },
    '&:hover fieldset': { borderColor: '#9ca3af' },
    '&.Mui-focused fieldset': { borderColor: accent }
  }
};

const formatRelativeTime = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const now = new Date();
  const diff = now - d;
  if (diff < 60000) return 'ahora';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m`;
  if (diff < 86400000) return d.toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit' });
  if (diff < 172800000) return 'Ayer';
  return d.toLocaleDateString('es', { day: '2-digit', month: '2-digit' });
};

const ChatPage = () => {
  const [searchParams] = useSearchParams();
  const {
    conversations, activeConversationId, setActiveConversationId,
    messagesCache, loadConversations, loadMessages, sendMessage,
    markAsRead, onlineUsers, typingUsers, hasMoreMessages, sendTyping, myId
  } = useChat();
  const { im } = useApi();
  const { user } = useAuth();
  const { canAction } = usePermissions();
  const isAdmin = user?.id === 'admin';
  console.log('[SUPERVISION DEBUG] user:', JSON.stringify({ id: user?.id, role: user?.role }), 'isAdmin:', isAdmin);

  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [supervisionConvs, setSupervisionConvs] = useState([]);
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [showInfoPanel, setShowInfoPanel] = useState(false);
  const [inputText, setInputText] = useState('');
  const [replyTo, setReplyTo] = useState(null);
  const [files, setFiles] = useState([]);
  const [emojiAnchor, setEmojiAnchor] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [showAddParticipant, setShowAddParticipant] = useState(false);
  const [viewerImage, setViewerImage] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);

  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const fileInputRef = useRef(null);
  const inputRef = useRef(null);
  const typingTimeout = useRef(null);
  const observerRef = useRef(null);
  const topSentinelRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);
  const audioStreamRef = useRef(null);

  const activeConv = useMemo(
    () => {
      const found = conversations.find(c => c.id === activeConversationId);
      if (found) return found;
      const supFound = supervisionConvs.find(c => c.id === activeConversationId) || null;
      console.log('[SUPERVISION DEBUG] activeConv lookup:', { activeConversationId, foundInConvs: !!found, foundInSupervision: !!supFound, supervisionConvsCount: supervisionConvs.length });
      return supFound;
    },
    [conversations, supervisionConvs, activeConversationId]
  );
  const messages = messagesCache[activeConversationId] || [];
  console.log('[SUPERVISION DEBUG] activeConv:', activeConv?.id, 'messages:', messages.length, 'filter:', filter);

  // Open conversation from URL params
  useEffect(() => {
    const convId = searchParams.get('conv');
    if (convId) setActiveConversationId(parseInt(convId));
  }, [searchParams]);

  // Load conversations on filter change
  useEffect(() => {
    setActiveConversationId(null);
    if (filter === 'supervision') {
      console.log('[SUPERVISION DEBUG] Loading all conversations...');
      im.getAllConversations().then(res => {
        console.log('[SUPERVISION DEBUG] getAllConversations response:', res.success, 'count:', res.data?.length, 'error:', res.message);
        if (res.success) setSupervisionConvs(res.data);
      }).catch(err => { console.error('[SUPERVISION DEBUG] getAllConversations ERROR:', err); });
    } else {
      loadConversations(filter === 'all' ? undefined : filter);
    }
  }, [filter]);

  // Load messages when active conversation changes
  useEffect(() => {
    console.log('[SUPERVISION DEBUG] activeConversationId changed:', activeConversationId, 'filter:', filter);
    if (activeConversationId) {
      if (!messagesCache[activeConversationId]) {
        console.log('[SUPERVISION DEBUG] Loading messages for:', activeConversationId);
        setLoadingMessages(true);
        loadMessages(activeConversationId).finally(() => setLoadingMessages(false));
      }
      if (filter !== 'supervision') markAsRead(activeConversationId);
    }
  }, [activeConversationId, filter]);

  // Scroll to bottom on new messages
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages.length]);

  // Load participants for info panel
  useEffect(() => {
    if (showInfoPanel && activeConversationId) {
      im.getParticipants(activeConversationId).then(res => {
        if (res.success) setParticipants(res.data);
      }).catch(() => {});
    }
  }, [showInfoPanel, activeConversationId]);

  // Infinite scroll for older messages
  useEffect(() => {
    if (!topSentinelRef.current || !activeConversationId) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMoreMessages[activeConversationId] && !loadingMessages) {
          const oldest = messages[0];
          if (oldest) {
            setLoadingMessages(true);
            loadMessages(activeConversationId, oldest.id).finally(() => setLoadingMessages(false));
          }
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(topSentinelRef.current);
    observerRef.current = observer;
    return () => observer.disconnect();
  }, [activeConversationId, messages, hasMoreMessages, loadingMessages]);

  const handleSend = async () => {
    const text = inputText.trim();
    if (!text && files.length === 0) return;
    if (!activeConversationId) return;

    const replyId = replyTo?.id;
    setInputText('');
    setReplyTo(null);
    setFiles([]);
    sendTyping(activeConversationId, false);

    try {
      await sendMessage(activeConversationId, text || null, files.length > 0 ? files : null, replyId);
    } catch (e) {
      console.error('Send failed:', e);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleInputChange = (e) => {
    setInputText(e.target.value);
    if (activeConversationId) {
      sendTyping(activeConversationId, true);
      if (typingTimeout.current) clearTimeout(typingTimeout.current);
      typingTimeout.current = setTimeout(() => {
        sendTyping(activeConversationId, false);
      }, 2000);
    }
  };

  const handleFileSelect = (e) => {
    const newFiles = Array.from(e.target.files || []);
    setFiles(prev => [...prev, ...newFiles].slice(0, 5));
    e.target.value = '';
  };

  const handlePaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    const pastedFiles = [];
    for (const item of items) {
      if (item.kind === 'file') {
        const file = item.getAsFile();
        if (file) pastedFiles.push(file);
      }
    }
    if (pastedFiles.length > 0) {
      e.preventDefault();
      setFiles(prev => [...prev, ...pastedFiles].slice(0, 5));
    }
  };

  const [isDragOver, setIsDragOver] = useState(false);
  const handleDragOver = (e) => { e.preventDefault(); setIsDragOver(true); };
  const handleDragLeave = () => setIsDragOver(false);
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const droppedFiles = Array.from(e.dataTransfer.files || []);
    if (droppedFiles.length > 0) {
      setFiles(prev => [...prev, ...droppedFiles].slice(0, 5));
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioStreamRef.current = stream;
      audioChunksRef.current = [];
      const recorder = new MediaRecorder(stream, { mimeType: MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : undefined });
      recorder.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      recorder.start();
      mediaRecorderRef.current = recorder;
      setIsRecording(true);
      setRecordingTime(0);
      recordingTimerRef.current = setInterval(() => setRecordingTime(t => t + 1), 1000);
    } catch (err) {
      console.error('Mic access denied:', err);
    }
  };

  const stopRecording = (send) => {
    if (!mediaRecorderRef.current) return;
    const recorder = mediaRecorderRef.current;
    recorder.onstop = async () => {
      if (send && audioChunksRef.current.length > 0 && activeConversationId) {
        const blob = new Blob(audioChunksRef.current, { type: recorder.mimeType || 'audio/webm' });
        const ext = (recorder.mimeType || '').includes('ogg') ? 'ogg' : 'webm';
        const file = new File([blob], `audio.${ext}`, { type: recorder.mimeType || 'audio/webm' });
        try {
          await sendMessage(activeConversationId, null, [file], null);
        } catch (e) { console.error('Send audio failed:', e); }
      }
      audioChunksRef.current = [];
    };
    recorder.stop();
    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach(t => t.stop());
      audioStreamRef.current = null;
    }
    mediaRecorderRef.current = null;
    clearInterval(recordingTimerRef.current);
    recordingTimerRef.current = null;
    setIsRecording(false);
    setRecordingTime(0);
  };

  const formatRecordingTime = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  const getConvName = (conv) => {
    if (conv.supervision_name) return conv.supervision_name;
    if (conv.conv_type === 'direct') {
      return conv.dm_peer?.name || 'Chat directo';
    }
    return conv.name || 'Grupo';
  };

  const getConvAvatar = (conv) => {
    if (conv.conv_type === 'direct') return conv.dm_peer?.avatar || '';
    return conv.avatar_path || '';
  };

  const isOnline = (conv) => {
    if (conv.conv_type === 'direct' && conv.dm_peer) {
      return onlineUsers.has(conv.dm_peer.agent_id);
    }
    return false;
  };

  const convTyping = activeConversationId ? typingUsers[activeConversationId] : null;
  const typingNames = convTyping && convTyping.size > 0
    ? Array.from(convTyping).map(id => {
        const p = participants.find(pp => pp.agent_id === id);
        return p?.name || id;
      })
    : [];

  const isSupervision = filter === 'supervision';
  const displayConversations = isSupervision ? supervisionConvs : conversations;

  // Permiso de envío según tipo de conversación activa
  const canSendInActiveConv = useMemo(() => {
    if (!activeConv) return false;
    if (!canAction('send_message_chat')) return false;
    if (activeConv.conv_type === 'direct') {
      // Si el peer tiene rol Agent, requiere permiso send_message_direct_chat
      if (activeConv.dm_peer?.role === 'Agent') {
        return canAction('send_message_direct_chat');
      }
      return true; // DM a otros roles siempre permitido
    }
    // group, auto_campaign, auto_skill, auto_team
    return canAction('send_message_group_chat');
  }, [activeConv, canAction]);

  const filteredConversations = displayConversations.filter(c => {
    if (!search) return true;
    const name = (isSupervision && c.supervision_name) ? c.supervision_name.toLowerCase() : getConvName(c).toLowerCase();
    return name.includes(search.toLowerCase());
  });

  const handleConversationCreated = (conv) => {
    setActiveConversationId(conv.id);
    loadConversations();
  };

  const handleLeaveGroup = async () => {
    if (!activeConversationId) return;
    try {
      await im.removeParticipant(activeConversationId, myId);
      setActiveConversationId(null);
      loadConversations();
    } catch (e) {
      console.error('Leave group error:', e);
    }
  };

  return (
    <Box sx={{
      height: 'calc(100vh - 64px)',
      maxHeight: 'calc(100vh - 64px)',
      display: 'flex',
      gap: 1.5,
      p: 2,
      boxSizing: 'border-box',
      overflow: 'hidden',
      background: '#dde1e7',
    }}>

      {/* Left panel - Conversation list */}
      <Box sx={{
        ...panel,
        width: 320, minWidth: 320, borderRadius: 3,
        display: 'flex', flexDirection: 'column',
        overflow: 'hidden', minHeight: 0,
      }}>
        <Box sx={{ p: 2, pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
            <Typography variant="h6" sx={{ color: '#1a1a2e', fontWeight: 600 }}>
              {isSupervision ? 'Supervisión' : 'Chat'}
            </Typography>
            {!isSupervision && canAction('create_conversation_chat') && <IconButton
              size="small"
              onClick={() => setShowNewDialog(true)}
              sx={{
                background: accent,
                color: '#fff',
                width: 32, height: 32,
                '&:hover': { background: '#6D28D9' },
                boxShadow: '0 2px 8px rgba(124,58,237,0.3)',
              }}
            >
              <AddIcon sx={{ fontSize: 20 }} />
            </IconButton>}
          </Box>
          <TextField
            fullWidth size="small"
            placeholder="Buscar..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#9ca3af', fontSize: 20 }} /></InputAdornment>
            }}
            sx={{ mb: 1, ...inputSx }}
          />
          <Box sx={{ display: 'flex', gap: 0.5, mb: 0.5 }}>
            {['all', 'direct', 'group'].map(f => (
              <Chip
                key={f}
                label={f === 'all' ? 'Todos' : f === 'direct' ? 'Directos' : 'Grupos'}
                size="small"
                onClick={() => setFilter(f)}
                sx={{
                  background: filter === f ? accentMid : '#f3f4f6',
                  color: filter === f ? accent : '#6b7280',
                  border: filter === f ? `1px solid ${accent}` : '1px solid transparent',
                  fontWeight: filter === f ? 600 : 400,
                  '&:hover': { background: filter === f ? accentMid : '#e5e7eb' }
                }}
              />
            ))}
            {canAction('supervise_chat') && (
              <Chip
                icon={<VisibilityIcon sx={{ fontSize: 14, color: filter === 'supervision' ? '#fff' : '#6b7280' }} />}
                label="Supervisión"
                size="small"
                onClick={() => setFilter(filter === 'supervision' ? 'all' : 'supervision')}
                sx={{
                  background: filter === 'supervision' ? '#ef4444' : '#f3f4f6',
                  color: filter === 'supervision' ? '#fff' : '#6b7280',
                  border: filter === 'supervision' ? '1px solid #ef4444' : '1px solid transparent',
                  fontWeight: filter === 'supervision' ? 600 : 400,
                  '&:hover': { background: filter === 'supervision' ? '#dc2626' : '#e5e7eb' }
                }}
              />
            )}
          </Box>
        </Box>

        <List sx={{ flex: 1, overflow: 'auto', px: 1 }}>
          {filteredConversations.map(conv => (
            <ListItem key={conv.id} disablePadding sx={{ mb: 0.3 }}>
              <ListItemButton
                selected={conv.id === activeConversationId}
                onClick={() => { console.log('[SUPERVISION DEBUG] Clicked conv:', conv.id, conv.name || conv.supervision_name); setActiveConversationId(conv.id); }}
                sx={{
                  borderRadius: 2, py: 1, px: 1.5,
                  '&.Mui-selected': { background: accentLight, border: `1px solid ${accentMid}` },
                  '&:hover': { background: '#f3f4f6' },
                }}
              >
                <ListItemAvatar sx={{ minWidth: 44 }}>
                  <Badge
                    overlap="circular"
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    badgeContent={
                      isOnline(conv) ? (
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#4caf50', border: '2px solid #fff' }} />
                      ) : null
                    }
                  >
                    <Avatar
                      src={getConvAvatar(conv) || undefined}
                      sx={{ width: 40, height: 40, bgcolor: accentMid, color: accent, fontSize: 16 }}
                    >
                      {conv.conv_type !== 'direct' ? <GroupIcon sx={{ fontSize: 20 }} /> : (getConvName(conv) || '?')[0]}
                    </Avatar>
                  </Badge>
                </ListItemAvatar>
                <ListItemText
                  primaryTypographyProps={{ component: 'div' }}
                  secondaryTypographyProps={{ component: 'div' }}
                  primary={
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" noWrap sx={{ color: '#1a1a2e', fontWeight: conv.unread_count > 0 ? 700 : 400, maxWidth: 150 }}>
                        {getConvName(conv)}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#9ca3af', fontSize: 10, flexShrink: 0 }}>
                        {formatRelativeTime(conv.last_message_at)}
                      </Typography>
                    </Box>
                  }
                  secondary={
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" noWrap sx={{
                        color: conv.unread_count > 0 ? '#374151' : '#9ca3af',
                        fontWeight: conv.unread_count > 0 ? 600 : 400,
                        maxWidth: 160
                      }}>
                        {conv.last_message_type === 'file' ? '📎 Archivo' :
                         conv.last_message_type === 'system' ? conv.last_message :
                         conv.last_message || 'Sin mensajes'}
                      </Typography>
                      {conv.unread_count > 0 && (
                        <Badge
                          badgeContent={conv.unread_count}
                          sx={{ '& .MuiBadge-badge': { fontSize: 10, minWidth: 18, height: 18, bgcolor: accent, color: '#fff' } }}
                        />
                      )}
                    </Box>
                  }
                />
              </ListItemButton>
            </ListItem>
          ))}
          {filteredConversations.length === 0 && (
            <Typography variant="body2" sx={{ color: '#9ca3af', textAlign: 'center', py: 4 }}>
              No hay conversaciones
            </Typography>
          )}
        </List>

      </Box>

      {/* Center panel - Messages */}
      <Box sx={{
        ...panel,
        flex: 0.65, borderRadius: 3,
        display: 'flex', flexDirection: 'column',
        overflow: 'hidden', minHeight: 0,
      }}>
        {activeConv ? (
          <>
            {/* Header */}
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 1.5,
              px: 2, py: 1.5,
              borderBottom: '1px solid #d1d5db',
            }}>
              <Avatar
                src={getConvAvatar(activeConv) || undefined}
                sx={{ width: 36, height: 36, bgcolor: accentMid, color: accent, fontSize: 14 }}
              >
                {activeConv.conv_type !== 'direct' ? <GroupIcon sx={{ fontSize: 18 }} /> : (getConvName(activeConv) || '?')[0]}
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body1" sx={{ color: '#1a1a2e', fontWeight: 600 }}>
                  {getConvName(activeConv)}
                </Typography>
                {activeConv.conv_type !== 'direct' && (
                  <Typography variant="caption" sx={{ color: '#9ca3af' }}>
                    {activeConv.participant_count || ''} participantes
                  </Typography>
                )}
                {activeConv.conv_type === 'direct' && isOnline(activeConv) && (
                  <Typography variant="caption" sx={{ color: '#4caf50' }}>En línea</Typography>
                )}
              </Box>
              <Tooltip title="Info">
                <IconButton
                  onClick={() => setShowInfoPanel(!showInfoPanel)}
                  sx={{ color: showInfoPanel ? accent : '#9ca3af' }}
                >
                  <InfoOutlinedIcon />
                </IconButton>
              </Tooltip>
            </Box>

            {/* Messages */}
            <Box
              ref={messagesContainerRef}
              onDragOver={!isSupervision ? handleDragOver : undefined}
              onDragLeave={!isSupervision ? handleDragLeave : undefined}
              onDrop={!isSupervision ? handleDrop : undefined}
              sx={{ flex: 1, overflow: 'auto', py: 1, display: 'flex', flexDirection: 'column', position: 'relative', backgroundImage: `linear-gradient(rgba(240,241,243,0.75), rgba(240,241,243,0.75)), url(${chatBgImg})`, backgroundRepeat: 'repeat', backgroundSize: 'auto' }}
            >
              {isDragOver && !isSupervision && (
                <Box sx={{
                  position: 'absolute', inset: 0, zIndex: 10,
                  background: 'rgba(124,58,237,0.12)',
                  border: `2px dashed ${accent}`,
                  borderRadius: 2,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Typography variant="h6" sx={{ color: accent, fontWeight: 600 }}>
                    Soltar archivos aquí
                  </Typography>
                </Box>
              )}
              <div ref={topSentinelRef} style={{ height: 1 }} />
              {loadingMessages && (
                <Box sx={{ textAlign: 'center', py: 2 }}>
                  <CircularProgress size={24} sx={{ color: accent }} />
                </Box>
              )}

              {messages.map((msg, i) => {
                const prevMsg = i > 0 ? messages[i - 1] : null;
                const showAvatar = !prevMsg || prevMsg.sender_id !== msg.sender_id || prevMsg.message_type === 'system';
                return (
                  <ChatMessageItem
                    key={msg.id}
                    message={msg}
                    isOwn={msg.sender_id === myId}
                    showAvatar={showAvatar}
                    onReply={isSupervision || !canAction('reply_message_chat') ? undefined : (m) => setReplyTo(m)}
                    onImageClick={(url, name) => setViewerImage({ url, name })}
                  />
                );
              })}

              {/* Typing indicator */}
              {typingNames.length > 0 && (
                <Box sx={{ px: 2, py: 0.5 }}>
                  <Typography variant="caption" sx={{ color: '#9ca3af', fontStyle: 'italic' }}>
                    {typingNames.join(', ')} {typingNames.length === 1 ? 'está' : 'están'} escribiendo...
                  </Typography>
                </Box>
              )}
              <div ref={messagesEndRef} />
            </Box>

            {/* Reply preview */}
            {!isSupervision && replyTo && (
              <Box sx={{
                mx: 2, px: 1.5, py: 0.8,
                borderLeft: `3px solid ${accent}`,
                background: accentLight,
                borderRadius: '0 8px 8px 0',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between'
              }}>
                <Box sx={{ overflow: 'hidden' }}>
                  <Typography variant="caption" sx={{ color: accent, fontWeight: 600 }}>
                    {replyTo.sender_name || replyTo.sender_id}
                  </Typography>
                  <Typography variant="caption" display="block" noWrap sx={{ color: '#6b7280' }}>
                    {replyTo.content}
                  </Typography>
                </Box>
                <IconButton size="small" onClick={() => setReplyTo(null)} sx={{ color: '#9ca3af' }}>
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
            )}

            {/* File previews */}
            {files.length > 0 && (
              <Box sx={{ mx: 2, display: 'flex', gap: 0.5, flexWrap: 'wrap', py: 0.5 }}>
                {files.map((f, i) => (
                  <Chip
                    key={i}
                    label={f.name}
                    size="small"
                    onDelete={() => setFiles(prev => prev.filter((_, idx) => idx !== i))}
                    sx={{
                      background: accentLight,
                      color: '#374151',
                      border: `1px solid ${accentMid}`,
                      '& .MuiChip-deleteIcon': { color: '#9ca3af' }
                    }}
                  />
                ))}
              </Box>
            )}

            {/* Input area / Supervision banner */}
            {isSupervision ? (
              <Box sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1,
                px: 2, py: 1.5,
                borderTop: '1px solid #d1d5db',
                background: '#fef2f2',
              }}>
                <VisibilityIcon sx={{ fontSize: 18, color: '#ef4444' }} />
                <Typography variant="body2" sx={{ color: '#ef4444', fontWeight: 500 }}>
                  Modo supervisión — solo lectura
                </Typography>
              </Box>
            ) : (
              <Box sx={{
                display: 'flex', alignItems: 'flex-end', gap: 0.5,
                px: 2, py: 1.5,
                borderTop: '1px solid #d1d5db',
              }}>
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: 'none' }}
                  onChange={handleFileSelect}
                  multiple
                />
                {isRecording ? (
                  /* Recording bar */
                  <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, gap: 1.5 }}>
                    <FiberManualRecordIcon sx={{ fontSize: 14, color: '#ef4444', animation: 'pulse 1s infinite', '@keyframes pulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.3 } } }} />
                    <Typography variant="body2" sx={{ color: '#ef4444', fontWeight: 600, fontFamily: 'monospace', minWidth: 40 }}>
                      {formatRecordingTime(recordingTime)}
                    </Typography>
                    <Box sx={{ flex: 1 }} />
                    <IconButton onClick={() => stopRecording(false)} sx={{ color: '#ef4444' }} title="Cancelar">
                      <DeleteIcon sx={{ fontSize: 22 }} />
                    </IconButton>
                    <IconButton onClick={() => stopRecording(true)} sx={{ color: '#4caf50' }} title="Enviar audio">
                      <SendIcon sx={{ fontSize: 22 }} />
                    </IconButton>
                  </Box>
                ) : (
                  /* Normal input */
                  <>
                    {canSendInActiveConv && canAction('upload_file_chat') && <IconButton
                      size="small"
                      onClick={() => fileInputRef.current?.click()}
                      sx={{ color: '#9ca3af', '&:hover': { color: accent } }}
                    >
                      <AttachFileIcon sx={{ fontSize: 20 }} />
                    </IconButton>}
                    {canSendInActiveConv && canAction('send_emoji_chat') && <IconButton
                      size="small"
                      onClick={(e) => setEmojiAnchor(e.currentTarget)}
                      sx={{ color: '#9ca3af', '&:hover': { color: accent } }}
                    >
                      <EmojiEmotionsIcon sx={{ fontSize: 20 }} />
                    </IconButton>}
                    <TextField
                      inputRef={inputRef}
                      fullWidth
                      multiline
                      maxRows={4}
                      size="small"
                      placeholder={canSendInActiveConv ? "Escribe un mensaje..." : "No tienes permiso para enviar mensajes"}
                      value={inputText}
                      onChange={handleInputChange}
                      onKeyDown={handleKeyDown}
                      onPaste={handlePaste}
                      disabled={!canSendInActiveConv}
                      sx={inputSx}
                    />
                    {canSendInActiveConv && ((inputText.trim() || files.length > 0) ? (
                      <IconButton onClick={handleSend} sx={{ color: accent }}>
                        <SendIcon sx={{ fontSize: 22 }} />
                      </IconButton>
                    ) : (
                      canAction('send_voice_chat') && <IconButton onClick={startRecording} sx={{ color: '#9ca3af', '&:hover': { color: accent } }}>
                        <MicIcon sx={{ fontSize: 22 }} />
                      </IconButton>
                    ))}
                  </>
                )}
              </Box>
            )}

            {!isSupervision && (
              <EmojiPickerPopover
                anchorEl={emojiAnchor}
                open={Boolean(emojiAnchor)}
                onClose={() => setEmojiAnchor(null)}
                onSelect={(emoji) => {
                  setInputText(prev => prev + emoji);
                  inputRef.current?.focus();
                }}
              />
            )}
          </>
        ) : (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 2 }}>
            <Typography variant="h5" sx={{ color: '#9ca3af', fontWeight: 300 }}>
              Selecciona una conversación
            </Typography>
            <Typography variant="body2" sx={{ color: '#9ca3af' }}>
              o crea una nueva con el botón +
            </Typography>
          </Box>
        )}
      </Box>

      {/* Right panel - Info */}
      {showInfoPanel && activeConv && (
        <Box sx={{
          ...panel,
          width: 280, minWidth: 280, borderRadius: 3,
          display: 'flex', flexDirection: 'column',
          overflow: 'hidden', minHeight: 0,
        }}>
          <Box sx={{ p: 2, textAlign: 'center', borderBottom: '1px solid #d1d5db' }}>
            <Avatar
              src={getConvAvatar(activeConv) || undefined}
              sx={{ width: 64, height: 64, mx: 'auto', mb: 1, bgcolor: accentMid, color: accent, fontSize: 28 }}
            >
              {activeConv.conv_type !== 'direct' ? <GroupIcon sx={{ fontSize: 32 }} /> : (getConvName(activeConv) || '?')[0]}
            </Avatar>
            <Typography variant="h6" sx={{ color: '#1a1a2e', fontSize: 16 }}>{getConvName(activeConv)}</Typography>
            {activeConv.conv_type === 'direct' && isOnline(activeConv) && (
              <Typography variant="caption" sx={{ color: '#4caf50' }}>En línea</Typography>
            )}
            {activeConv.conv_type !== 'direct' && (
              <Typography variant="caption" sx={{ color: '#9ca3af' }}>
                {activeConv.conv_type.startsWith('auto_') ? 'Grupo automático' : 'Grupo'}
              </Typography>
            )}
          </Box>

          {activeConv.conv_type !== 'direct' && (
            <Box sx={{ flex: 1, overflow: 'auto' }}>
              <Box sx={{ px: 2, py: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>
                  Participantes ({participants.length})
                </Typography>
                {(activeConv.my_role === 'admin' || ['admin', 'superadmin'].includes(myId)) && canAction('manage_participants_chat') && (
                  <Tooltip title="Agregar">
                    <IconButton size="small" onClick={() => setShowAddParticipant(true)} sx={{ color: accent }}>
                      <PersonAddIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
              <List dense sx={{ px: 1 }}>
                {participants.map(p => (
                  <ListItem key={p.agent_id} disablePadding>
                    <ListItemButton sx={{ borderRadius: 1, py: 0.5, '&:hover': { background: '#f3f4f6' } }}>
                      <ListItemAvatar sx={{ minWidth: 36 }}>
                        <Badge
                          overlap="circular"
                          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                          badgeContent={
                            onlineUsers.has(p.agent_id) ? (
                              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#4caf50', border: '2px solid #fff' }} />
                            ) : null
                          }
                        >
                          <Avatar src={p.avatar || undefined} sx={{ width: 28, height: 28, fontSize: 11, bgcolor: accentMid, color: accent }}>
                            {(p.name || '?')[0]}
                          </Avatar>
                        </Badge>
                      </ListItemAvatar>
                      <ListItemText
                        primary={p.name}
                        secondary={p.role === 'admin' ? 'Admin' : null}
                        primaryTypographyProps={{ sx: { color: '#1a1a2e', fontSize: 13 } }}
                        secondaryTypographyProps={{ sx: { color: accent, fontSize: 10 } }}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>

              <Divider sx={{ mx: 2, my: 1 }} />

              {canAction('leave_group_chat') && <Box sx={{ px: 2, pb: 2 }}>
                <ListItemButton
                  onClick={handleLeaveGroup}
                  sx={{ borderRadius: 1, color: '#ef5350', '&:hover': { background: 'rgba(239,83,80,0.08)' } }}
                >
                  <ExitToAppIcon sx={{ fontSize: 18, mr: 1 }} />
                  <Typography variant="body2">Salir del grupo</Typography>
                </ListItemButton>
              </Box>}

            </Box>
          )}
        </Box>
      )}

      <NewConversationDialog
        open={showNewDialog}
        onClose={() => setShowNewDialog(false)}
        onCreated={handleConversationCreated}
        canCreateGroup={canAction('create_group_chat')}
        canMessageAgents={canAction('send_message_direct_chat')}
      />

      {/* Image Viewer Modal */}
      <Dialog
        open={!!viewerImage}
        onClose={() => setViewerImage(null)}
        maxWidth={false}
        PaperProps={{ sx: { background: 'rgba(0,0,0,0.92)', boxShadow: 'none', m: 0, maxWidth: '100vw', maxHeight: '100vh', width: '100vw', height: '100vh', borderRadius: 0 } }}
      >
        {viewerImage && (
          <Box sx={{ width: '100vw', height: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
            <Box sx={{ position: 'absolute', top: 16, right: 16, display: 'flex', gap: 1, zIndex: 1 }}>
              <IconButton
                component="a"
                href={viewerImage.url}
                download={viewerImage.name || 'image'}
                sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}
              >
                <DownloadIcon />
              </IconButton>
              <IconButton
                onClick={() => setViewerImage(null)}
                sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
            <Box
              component="img"
              src={viewerImage.url}
              alt={viewerImage.name}
              sx={{ maxWidth: '90vw', maxHeight: '85vh', objectFit: 'contain', borderRadius: 1 }}
            />
            {viewerImage.name && (
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mt: 1 }}>
                {viewerImage.name}
              </Typography>
            )}
          </Box>
        )}
      </Dialog>
    </Box>
  );
};

export default ChatPage;
